import Tr from "translations/Translations";
import Loading from "components/Loading";
import SearchBar from "./AuditTaskAllowedObjectSearchBar";
import SimpleTable from "components/complex/simpleTable/SimpleTable";
import Pagination from "components/Pagination";
import Dialog from "components/Dialog";
import React, {useEffect, useMemo, useState} from "react";
import {connect} from "react-redux";
import {Checkbox, withStyles} from "@material-ui/core";
import useSearch from "../../../../hooks/useSearch";
import AuditTaskElementsService from "../service/AuditTaskElementsService";

const style = theme => ({
    root: {
        padding: theme.spacing(2)
    },
    number: {
        width: 250
    },
    status: {
        width: 350
    },
    gridNew: {
        padding: theme.spacing(0.5)
    },
    icon: {
        marginRight: theme.spacing(1)
    },
    buttonNew: {
        marginRight: theme.spacing(2)
    },
    cell: {
        padding: theme.spacing(1),
    },
    checkbox: {
        width: 30
    }
})

function AuditTaskAllowedObjectPopup({
    open,
    taskId,
    excludeObjectIds,
    objectType,
    onConfirm,
    onCancel,
    auditAllowedObjectList = {},
    classes
}) {

    const { items } = auditAllowedObjectList;

    const [loading, setLoading] = useState(false);
    const [chosenObjects, setChosenObjects] = useState([]);
    const [searchState, setSearchState] = useState(0);
    const searchInitValues = {number: '', name: '', dateStart: null, dateEnd: null, orderType: ''}
    const sortInitValues = {column: 'identity', direction: 'desc'}

    const title = !!objectType ? Tr('audit_task_allowed_objects_' + objectType.toLowerCase()) : '';

    const {searchData, totalCount, setTotalCount, setPaginationConfig, setSearchConfig, setSortConfig,}
        = useSearch({
        search: {
            filters: {...searchInitValues}
        },
        sort: {...sortInitValues}
    });

    const setFilters = (filters) => {
        const newFilters = {...searchData.search.filters, ...filters};
        setSearchConfig({...searchData.search, filters: newFilters});
    }

    useEffect(() => {
        return () => {
            AuditTaskElementsService.clearAllowedObjects();
        }
    }, []);

    useEffect(() => {
        fetchObjects();
        // eslint-disable-next-line
    }, [searchState])

    const fetchObjects = (type) => {
        setLoading(true);
        const {search, pagination, sort} = searchData;
        const {page, pageSize} = pagination;
        AuditTaskElementsService.getAllowedObjects(taskId, objectType, excludeObjectIds, page, pageSize, {
            ...search.filters,
            ...search.params,
        }, sort).then(resp => {
            setTotalCount(resp.totalCount);
            setLoading(false)
        });
    }

    const doSearch = () => {
        setChosenObjects([]);
        setSearchState(searchState + 1);
    }

    const handleSortChange = (id) => {
        let direction = 'desc';
        const active = searchData.sort.column === id;
        if (active) {
            direction = searchData.sort.direction === 'asc' ? 'desc' : 'asc';
        }

        setSortConfig({
            column: id,
            direction
        })
    }

    const getSortProps = (id) => {
        return {
            active: searchData.sort.column === id,
            direction: searchData.sort.direction,
            onClick: () => {
                handleSortChange(id)
            }
        }
    }

    const getHeaders = () => {
        if (!items) return;
        const headers = [];
        headers.push('');
        headers.push({
            value: Tr('audit_task_allowed_objects_number_' + objectType.toLowerCase()),
            sort: getSortProps('number')
        });
        headers.push({
            value: Tr('audit_task_allowed_objects_name_' + objectType.toLowerCase()),
            sort: getSortProps('name')
        });
        headers.push({
            value: Tr('audit_task_allowed_objects_status_' + objectType.toLowerCase()),
            sort: getSortProps('status')
        });
        return headers;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const headers = useMemo(getHeaders, [items]);

    const getHeaderClasses = () => {
        if (!items) return;
        const headers = [];
        headers.push(classes.checkbox);
        headers.push(classes.number);
        headers.push(undefined);
        headers.push(classes.status);
        return headers;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const headerClasses = useMemo(getHeaderClasses, [items]);

    const checkObject = (identity) => {
        if (chosenObjects.some(x => x.objectIdentity === Number(identity)))
            chosenObjects.splice(chosenObjects.indexOf(chosenObjects.filter(i => i.objectIdentity === Number(identity))[0]), 1);
        else
            chosenObjects.push(items.filter(i => i.objectIdentity === Number(identity))[0]);
        setChosenObjects(chosenObjects);
    }

    const getRows = () => {
        if (!items) return;
        const rows = items.map((item) => {
            const row = [];
            row.push(<Checkbox
                edge="start"
                tabIndex={-1}
                onChange={(e) => checkObject(e.target.value)}
                value={item.objectIdentity}
            />);
            row.push(item.objectNumber);
            row.push(item.objectName);
            row.push(item.objectStatus);
            return row;
        });
        return rows;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const rows = useMemo(getRows, [items]);

    const handleSearch = () => {
        setSearchState(searchState + 1);
    }

    const handleReset = () => {
        setFilters({...searchInitValues});
        setSortConfig({...sortInitValues});
        handleSearch();
    }

    const handlePaginationChange = (data) => {
        setPaginationConfig(data);
        doSearch();
    }

    const pickObjects = () => {
        onConfirm(chosenObjects);
    }

    return (
        <Dialog title={title} open={open} maxWidth={'xl'} onClose={onCancel}>
            {!items && <Loading/>}
            {!!items && <div className={classes.root}>
                <SearchBar
                    pickObjects={() => pickObjects()}
                    objectType={objectType}
                    setFilters={setFilters}
                    filters={searchData.search.filters}
                    onSubmit={handleSearch}
                    onReset={handleReset}
                    searching={loading}
                />
                <SimpleTable
                    headers={headers}
                    items={rows}
                    columnClasses={headerClasses}
                    cellClassName={classes.cell}
                />
            </div>}

            <Pagination config={searchData.pagination} setConfig={handlePaginationChange} totalCount={totalCount}/>
        </Dialog>
    );

}

export default connect((state) => ({
    auditAllowedObjectList: state.resources.auditAllowedObjectListPopup
}), {})(withStyles(style)(AuditTaskAllowedObjectPopup));